import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { foundersApi, formatApiError } from "../lib/api";

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  dni: "",
  birth_date: "",
  comments: "",
  accept_terms: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s\-]{6,20}$/;
const DNI_RE = /^[A-Z0-9]{6,12}$/i;

export default function ReserveDialog({ open, onClose, onSuccess }) {
  const { t } = useLang();
  const r = t.reserve;

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  if (!open) return null;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = r.errors.required;
    if (!form.last_name.trim()) e.last_name = r.errors.required;
    if (!form.email.trim()) e.email = r.errors.required;
    else if (!EMAIL_RE.test(form.email.trim())) e.email = r.errors.email;
    if (!form.phone.trim()) e.phone = r.errors.required;
    else if (!PHONE_RE.test(form.phone.trim())) e.phone = r.errors.phone;
    if (!form.dni.trim()) e.dni = r.errors.required;
    else if (!DNI_RE.test(form.dni.trim())) e.dni = r.errors.dni;
    if (!form.birth_date) e.birth_date = r.errors.required;
    if (!form.accept_terms) e.accept_terms = r.errors.terms;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = await foundersApi.register({
        ...form,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        dni: form.dni.trim().toUpperCase(),
      });
      setSuccess({ plaza_number: data.plaza_number });
      onSuccess?.(data);
    } catch (err) {
      setServerError(formatApiError(err?.response?.data?.detail) || "Error de conexión");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(initialForm);
    setErrors({});
    setServerError("");
    setSuccess(null);
    onClose?.();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-stretch md:items-center justify-center md:p-4"
        data-testid="reserve-dialog-overlay"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full md:max-w-2xl bg-black md:border md:border-[#FFCC00]/30 flex flex-col h-full md:h-auto md:max-h-[92vh]"
          data-testid="reserve-dialog"
        >
          {success ? (
            <div className="p-8 md:p-14 text-center flex-1 flex flex-col justify-center">
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 w-10 h-10 border border-white/20 flex items-center justify-center text-white hover:border-[#FFCC00] hover:text-[#FFCC00]"
                aria-label="close"
                data-testid="reserve-close"
              >
                <X size={18} />
              </button>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFCC00] text-black mb-6 mx-auto">
                <Check size={28} strokeWidth={3} />
              </div>
              <h3 className="font-display text-3xl md:text-5xl text-white mb-3">
                {r.success_title}
              </h3>
              <p className="text-white/70 mb-2 font-body text-sm md:text-base">{r.success_text(success.plaza_number)}</p>
              <div className="font-display text-6xl md:text-8xl text-[#FFCC00] my-6 md:my-8">
                #{String(success.plaza_number).padStart(3, "0")}
              </div>
              <button
                onClick={handleClose}
                data-testid="reserve-success-close"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#FFCC00] text-black font-bold text-xs tracking-[0.22em] uppercase hover:bg-white transition-colors mx-auto"
              >
                {r.success_cta}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col h-full md:h-auto md:max-h-[92vh]">
              {/* Sticky header */}
              <div className="sticky top-0 z-10 bg-black/95 backdrop-blur-md border-b border-white/10 px-5 md:px-10 py-4 md:py-5 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-bold text-[#FFCC00] mb-1">
                    <Sparkles size={11} className="fill-[#FFCC00]" />
                    <span className="truncate">Oferta especial</span>
                  </div>
                  <h3 className="font-display text-xl md:text-3xl text-white leading-tight">
                    {r.title}
                  </h3>
                  <p className="hidden md:block text-xs text-white/55 mt-1 font-body">{r.subtitle}</p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-9 h-9 md:w-10 md:h-10 shrink-0 border border-white/20 flex items-center justify-center text-white hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
                  aria-label="close"
                  data-testid="reserve-close"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto px-5 md:px-10 py-5 md:py-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <Field
                    testId="field-first-name"
                    label={r.first_name}
                    value={form.first_name}
                    onChange={(v) => setField("first_name", v)}
                    placeholder={r.placeholders.first_name}
                    error={errors.first_name}
                    required
                  />
                  <Field
                    testId="field-last-name"
                    label={r.last_name}
                    value={form.last_name}
                    onChange={(v) => setField("last_name", v)}
                    placeholder={r.placeholders.last_name}
                    error={errors.last_name}
                    required
                  />
                  <Field
                    testId="field-email"
                    label={r.email}
                    type="email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    placeholder={r.placeholders.email}
                    error={errors.email}
                    required
                  />
                  <Field
                    testId="field-phone"
                    label={r.phone}
                    type="tel"
                    value={form.phone}
                    onChange={(v) => setField("phone", v)}
                    placeholder={r.placeholders.phone}
                    error={errors.phone}
                    required
                  />
                  <Field
                    testId="field-dni"
                    label={r.dni}
                    value={form.dni}
                    onChange={(v) => setField("dni", v)}
                    placeholder={r.placeholders.dni}
                    error={errors.dni}
                    required
                  />
                  <Field
                    testId="field-birth-date"
                    label={r.birth_date}
                    type="date"
                    value={form.birth_date}
                    onChange={(v) => setField("birth_date", v)}
                    error={errors.birth_date}
                    required
                  />
                </div>

                <div className="mt-3 md:mt-4">
                  <Field
                    testId="field-comments"
                    label={r.comments}
                    value={form.comments}
                    onChange={(v) => setField("comments", v)}
                    placeholder={r.placeholders.comments}
                    textarea
                  />
                </div>

                {serverError && (
                  <div
                    data-testid="reserve-server-error"
                    className="mt-4 flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-xs md:text-sm"
                  >
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <span>{serverError}</span>
                  </div>
                )}
              </div>

              {/* Sticky footer with terms + submit */}
              <div className="sticky bottom-0 z-10 bg-black/95 backdrop-blur-md border-t border-white/10 px-5 md:px-10 py-4 md:py-5 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <label className="flex items-start gap-2 cursor-pointer group mb-3" data-testid="field-terms">
                  <input
                    type="checkbox"
                    checked={form.accept_terms}
                    onChange={(e) => setField("accept_terms", e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#FFCC00] cursor-pointer shrink-0"
                  />
                  <span className="text-[11px] md:text-xs text-white/65 leading-snug group-hover:text-white/85 transition-colors">
                    {r.accept_terms}
                  </span>
                </label>
                {errors.accept_terms && (
                  <div className="text-[11px] text-red-400 mb-2 ml-6" data-testid="error-terms">
                    {errors.accept_terms}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reserve-submit"
                  className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 md:py-4 bg-[#FFCC00] text-black font-bold text-xs md:text-sm tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {r.submitting}
                    </>
                  ) : (
                    <>
                      {r.submit}
                      <span className="inline-block w-5 h-px bg-current" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", error, required, textarea, testId }) {
  const base =
    "w-full bg-[#0a0a0a] border border-white/15 text-white placeholder:text-white/30 px-3 py-2.5 md:px-4 md:py-3 text-sm focus:outline-none focus:border-[#FFCC00] transition-colors";
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/60 mb-1.5">
        {label} {required && <span className="text-[#FFCC00]">*</span>}
      </span>
      {textarea ? (
        <textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={base + " resize-none"}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={base + (type === "date" ? " [color-scheme:dark]" : "")}
        />
      )}
      {error && (
        <span className="block text-[11px] text-red-400 mt-1" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}
